import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Pagination,
  Button,
  Space,
  DatePicker,
  Typography,
  Row,
  Col,
  Divider,
  Tag,
  Select,
} from 'antd'
import {
  DownloadOutlined,
  FilterOutlined,
  ReloadOutlined,
  DollarOutlined,
  BookOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import useCourseEnrollmentsReport from '../Hooks/Reports/useEnrollmentCourse'
import { CourseRankingSort } from 'Common/Constants/Enums/Individual/Reports/CourseRankingSortEnum'

const { RangePicker } = DatePicker
const { Title, Text } = Typography
const { Option } = Select

export default function CourseEnrollmentsReport() {
  const {
    data,
    loading,
    isDownloading, // Use this from hook
    pageNumber,
    pageSize,
    totalCount,
    filters,
    setFilters,
    setPageNumber,
    setPageSize,
    fetchReport,
    downloadReport,
  } = useCourseEnrollmentsReport()

  // Preserving original local filter logic
  const [localFilters, setLocalFilters] = useState({
    SortBy: CourseRankingSort.Enrollments,
    FromDate: null,
    ToDate: null,
    IsPaid: false,
  })

  // Preserving original useEffect for syncing (Updated to be safer)
  useEffect(() => {
    setLocalFilters(prev => ({
      ...prev,
      ...filters,
    }))
  }, [filters])

  // Preserving original fetch effect logic
  useEffect(() => {
    const params = {
      PageNumber: pageNumber,
      PageSize: pageSize,
      SortBy: filters.SortBy,
    }

    if (filters.FromDate) params.FromDate = dayjs(filters.FromDate).format('YYYY-MM-DD')
    if (filters.ToDate) params.ToDate = dayjs(filters.ToDate).endOf('day').format('YYYY-MM-DD')
    if (filters.IsPaid !== undefined) params.IsPaid = filters.IsPaid

    fetchReport(params)
  }, [pageNumber, pageSize, filters])

  const handleApply = () => {
    setPageNumber(1)
    setFilters({ ...localFilters })
  }

  const handleReset = () => {
    const defaultFilters = {
      SortBy: CourseRankingSort.Enrollments,
      FromDate: null,
      ToDate: null,
      IsPaid: false,
    }
    setLocalFilters(defaultFilters)
    setPageNumber(1)
    setFilters(defaultFilters)
  }

  const handleDownload = () => {
    const params = { SortBy: filters.SortBy }
    if (filters.FromDate) params.FromDate = dayjs(filters.FromDate).format('YYYY-MM-DD')
    if (filters.ToDate) params.ToDate = dayjs(filters.ToDate).endOf('day').format('YYYY-MM-DD')
    if (filters.IsPaid !== undefined) params.IsPaid = filters.IsPaid

    downloadReport(params)
  }

  const handlePaginationChange = (p, s) => {
    setPageNumber(p)
    setPageSize(s)
  }

  // RESTORED ALL YOUR PREVIOUS COLUMNS EXACTLY
  const columns = [
    { title: 'Title', dataIndex: 'title', key: 'title', width: 200 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <Space>
          {record.isPaid ? (
            <Tag color="green" icon={<DollarOutlined />}>${price}</Tag>
          ) : (
            <Tag color="blue">Free</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Access Duration',
      dataIndex: 'accessDurationDays',
      key: 'accessDurationDays',
      render: (days) => `${days} days`,
    },
    {
      title: (
        <Space><BookOutlined />Enrollments</Space>
      ),
      key: 'enrollments',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>In Progress: {record.enrollInprogressCount}</div>
          <div>Completed: {record.enrollCompletedCount}</div>
        </Space>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
  ]

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Course Enrollments Report</Title>
          <Text type="secondary">Financial overview and enrollment tracking across courses</Text>
        </div>
        <Space>
          <Button onClick={() => window.history.back()}>Back</Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={isDownloading} // STOP DOUBLE CLICK
          >
            Download
          </Button>
        </Space>
      </div>

      <Card style={{ marginTop: 16 }} title={<Space><FilterOutlined />Filters</Space>}>
        <Row gutter={16}>
          <Col md={8}>
            <Text strong>Course Creation Period</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={localFilters.FromDate && localFilters.ToDate ? [dayjs(localFilters.FromDate), dayjs(localFilters.ToDate)] : null}
              onChange={(d) => setLocalFilters({
                ...localFilters,
                FromDate: d?.[0]?.toDate() || null,
                ToDate: d?.[1]?.toDate() || null,
              })}
            />
          </Col>

          <Col md={4}>
            <Text strong>Paid</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={localFilters.IsPaid}
              onChange={(value) => setLocalFilters({ ...localFilters, IsPaid: value })}
            >
              <Option value={undefined}>All</Option>
              <Option value={true}>Paid</Option>
              <Option value={false}>Free</Option>
            </Select>
          </Col>

          <Col md={8}>
            <Text strong>Sort By</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              value={localFilters.SortBy || CourseRankingSort.Enrollments}
              onChange={(value) => setLocalFilters({ ...localFilters, SortBy: value })}
            >
              <Option value={CourseRankingSort.Enrollments}>Enrollments</Option>
              <Option value={CourseRankingSort.Price}>Price</Option>
              <Option value={CourseRankingSort.EstimatedTime}>Estimated Time</Option>
              <Option value={CourseRankingSort.Latest}>Latest</Option>
            </Select>
          </Col>
        </Row>
        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
          <Button type="primary" onClick={handleApply}>Apply</Button>
        </Space>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
        />
        <Divider />
        <Pagination
          current={pageNumber}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          onChange={handlePaginationChange}
        />
      </Card>
    </div>
  )
}